export const SERIES_VARS = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-6)",
  "var(--series-7)",
  "var(--series-8)",
] as const;

/**
 * Categorical hues are assigned in fixed order and never cycled. A ninth series
 * folds into "Other" rather than reusing slot 1, so a filter that changes the
 * series count can never repaint the survivors.
 */
export function seriesColor(index: number): string {
  return SERIES_VARS[Math.min(index, SERIES_VARS.length - 1)]!;
}

export function money(value: number, decimals = 2): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Per-unit price at two decimals.
 *
 * Bulk goods sold by the stack work out to fractions of a cent per unit, and
 * four decimal places to express that is noise on a dashboard. Anything that
 * would round to zero is shown as "under a cent" instead, with the real figure
 * available as the batch price beside it - which is the number on the sign, and
 * the one the owner actually set.
 */
export function unitMoney(value: number): string {
  if (value > 0 && value < 0.005) return "<$0.01";
  return money(value);
}

/** "$4.99 for 64" - the sign as written. */
export function batchPrice(
  signPrice: number | null,
  batchQty: number,
): string | null {
  if (signPrice == null) return null;
  return batchQty > 1
    ? `${money(signPrice)} for ${number(batchQty)}`
    : money(signPrice);
}

/**
 * Tax, shown to whatever precision it takes to not read as nothing.
 *
 * Two decimals is right for money owed and wrong for money quoted: a sign
 * selling 64 for $9.99 owes $0.2498, and at two decimals a $0.16 item appears
 * to owe nothing at all. So the width follows the number.
 *
 * Two decimals when they are exact, because $0.30 should not render $0.3000.
 * Otherwise three when the first three decimals are all significant, four when
 * they are not - and always rounded up, since the point of the exercise is
 * that a tax which exists must never display as zero.
 */
export function taxMoney(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "$0.00";

  const ceilTo = (decimals: number) => {
    const factor = 10 ** decimals;
    return (Math.ceil(value * factor) / factor).toFixed(decimals);
  };

  // Exact at two decimals - the ordinary case, and it should look ordinary.
  // Compared with a tolerance because 2.5% of $12 is 0.30000000000000004 in
  // binary floating point, and an equality test sends $0.30 down the
  // four-decimal path to render $0.3001.
  if (Math.abs(value - Math.round(value * 100) / 100) < 1e-9) {
    return `$${value.toFixed(2)}`;
  }

  const [d1, d2, d3] = value.toFixed(4).split(".")[1]!.slice(0, 3).split("");
  const allSignificant = d1 !== "0" && d2 !== "0" && d3 !== "0";
  return `$${ceilTo(allSignificant ? 3 : 4)}`;
}

/** Compact form for axis ticks and tiles, where two decimals are noise. */
export function moneyShort(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000)
    return `$${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000)
    return `$${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `$${value.toFixed(0)}`;
}

export function number(value: number): string {
  return value.toLocaleString("en-US");
}

export function percent(value: number, decimals = 1): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(decimals)}%`;
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function shortDate(value: string): string {
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function hourLabel(hour: number): string {
  if (hour === 0) return "12a";
  if (hour === 12) return "12p";
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}
